import {createSelector} from 'reselect'
import {createSelectQuery} from 'containers/App/selectors'
import {makeFullName} from 'utils/make-fullname'

export const selectProfileType = (state) => state.user.type
export const selectDisplay = (state) => state.user.display
export const selectInfo = (state) => state.user.info
export const selectProfile = (state) => state.user.profile
export const selectUnconfirmedEmails = (state) =>
  state.user.info.emails.unconfirmed

export const selectAccounts = createSelector(
  selectInfo,
  (info) => info && info.rawAccounts && info.rawAccounts.accounts
)

export const selectProblematicCreds = createSelector(selectInfo, (info) =>
  info && info.problematicCreds
    ? info.problematicCreds.map(({account, ...rest}) => {
        return {
          ...rest,
          account: account.replace(/#/, '@') // <---  rawEmails come with the '#' symbol, but ramMails come with '@' symbol
        }
      })
    : []
)

export const selectBlockedRamblerMail = createSelector(
  selectInfo,
  selectProblematicCreds,
  (info, problematicCreds) =>
    problematicCreds.reduce(
      (acc, cred) => (cred.account === info.email ? {...acc, ...cred} : acc),
      {}
    )
)

export const selectChainId = createSelector(
  selectInfo,
  (info) => info && info.chainId
)

export const selectEmails = createSelector(
  selectProfileType,
  selectInfo,
  selectAccounts,
  selectProblematicCreds,
  (type, info, accounts, problematicCreds) =>
    accounts
      ? accounts
          .filter(
            ({accountId, provider, accountType}) =>
              provider === 'passport.rambler.ru' &&
              (accountType === 'EMAIL' ||
                accountType === 'UNCONFIRMED_MAIL' ||
                (accountType === 'DEFAULT' && accountId.match(/#/)))
          )
          .map(({accountId, ...rest}) => {
            accountId = accountId.replace(/#/, '@')

            let tempObj = {}

            problematicCreds.forEach(({account, status}) => {
              if (account === accountId) {
                tempObj.status = status
              }
            })

            return {
              ...rest,
              ...tempObj,
              accountId
            }
          })
          .filter(
            ({accountId}) => type !== 'general' || info.email !== accountId
          )
      : []
)

export const selectEmailCredentials = createSelector(
  selectInfo,
  (info) => info && info.emailCredentials
)

export const selectPhones = createSelector(selectAccounts, (accounts) =>
  accounts
    ? accounts
        .filter(
          ({provider, accountType, passwordType}) =>
            provider === 'passport.rambler.ru' &&
            (accountType === 'PHONE' || passwordType === 'PHONE')
        )
        .map(({accountId}) => ({
          accountId
        }))
    : []
)

export const selectRawSocial = createSelector(
  selectInfo,
  (info) => info && info.socialProfiles
)

export const selectSocialProfiles = createSelector(
  selectInfo,
  selectProfile,
  ({socialProfiles, phones, email}, {firstname, lastname}) =>
    socialProfiles
      ? socialProfiles
          .filter(({isSelf, provider}) => {
            // Всегда показываем данные про сбер
            if (provider === 'sberbank') {
              return true
            }

            return isSelf !== 1
          })
          .map(({properties = {}, extUid = '', isSelf}) => {
            const [id, provider] = extUid.split('@')

            return {
              id,
              provider,
              isSelf,
              oauthAvatar:
                properties.oauthAvatar ||
                '//avatars.rambler.ru/100x100/default',
              displayName:
                properties.firstname || properties.lastname
                  ? makeFullName(properties.firstname, properties.lastname)
                  : makeFullName(firstname, lastname),
              ...(provider === 'sberbank'
                ? {
                    oauthEmail: properties.oauthEmail || email,
                    phone: properties.phone || phones[0]
                  }
                : {})
            }
          })
      : []
)

export const selectUnconfirmedEmail = createSelector(
  selectProfileType,
  selectEmails,
  (type, accounts) => {
    if (type !== 'service' || !accounts || accounts.length === 0) {
      return null
    }

    const [first] = accounts

    return first && first.accountType !== 'EMAIL' ? first : null
  }
)

export const selectRawChampionat = createSelector(
  selectInfo,
  (info) => info && info.championat
)

export const selectChampionat = createSelector(
  selectRawChampionat,
  (championat) => (championat && championat.all) || []
)

export const selectDefaultChampionat = createSelector(
  selectRawChampionat,
  (championat) =>
    championat && championat.default && championat.default.accountId
)

export const selectWebAuthnKeys = createSelector(
  selectInfo,
  (info) => info && info.webAuthnKeys
)

export const selectSessionCredentials = createSelector(
  selectInfo,
  (info) => info && info.session && info.session.credentials
)

export const selectIsAnyMailBlocked = createSelector(
  selectEmailCredentials,
  selectProfileType,
  (emailCredentials, type) =>
    emailCredentials &&
    emailCredentials.length === 0 &&
    (type === 'general' || type === 'service')
)

export const selectIsPrimaryMailBlocked = createSelector(
  selectEmailCredentials,
  selectInfo,
  selectSessionCredentials,
  selectProfileType,
  (emailCredentials, info, sessionCredentials, type) => {
    const emailAccounts =
      emailCredentials &&
      emailCredentials.map((credential) => credential.account)

    return (
      (emailCredentials &&
        info &&
        sessionCredentials &&
        !emailAccounts.includes(info.email) &&
        !emailAccounts.includes(sessionCredentials.account) &&
        type === 'general') ||
      (emailCredentials && emailCredentials.length === 0 && type === 'service')
    )
  }
)

export const selectHideNavigation = createSelector(
  createSelectQuery('param'),
  (param) => ['embed', 'iframe', 'popup'].includes(param)
)
